import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { getUserProfile } from '../utils/firestoreUser';
import './IcebreakerModal.css';

const activityTypes = [
  { label: 'Indoor', value: 'indoor' },
  { label: 'Outdoor', value: 'outdoor' },
  { label: 'Either', value: 'either' },
];

export default function IcebreakerModal({ open, onClose, dinnerId }) {
  const [activityType, setActivityType] = useState('either');
  const [suitableForChildren, setSuitableForChildren] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [contextData, setContextData] = useState(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const { currentUser } = useAuth();
  const resultsSectionRef = useRef(null);

  // Load user context data
  useEffect(() => {
    const loadContextData = async () => {
      if (!currentUser || !open || !dinnerId) return;
      
      setLoadingContext(true);
      try {
        console.log('Loading context data for dinner:', dinnerId);
        
        // Get current user's data
        const userProfile = await getUserProfile(currentUser.uid);
        const answerRef = doc(db, 'shabbotAnswers', currentUser.uid);
        const answerSnap = await getDoc(answerRef);
        const userAnswers = answerSnap.exists() ? answerSnap.data().answers : {};

        // Get dinner data
        const dinnerRef = doc(db, 'dinners', dinnerId);
        const dinnerSnap = await getDoc(dinnerRef);
        const dinnerData = dinnerSnap.exists() ? dinnerSnap.data() : {};

        console.log('Dinner data:', dinnerData);
        console.log('User answers:', userAnswers);

        // Determine if current user is host or guest
        const isHost = currentUser.uid === dinnerData.hostId;
        
        let hostInfo, guestInfo;
        if (isHost) {
          // Current user is host
          hostInfo = { 
            signingInAs: userAnswers.signingInAs || "Host",
            shabbotAnswers: userAnswers 
          };
          
          // Try to get guest information from accepted dinner requests
          let guestSigningAs = "Guest";
          let guestAnswers = {};
          let hasFamily = false;
          
          try {
            // Query for accepted dinner requests for this dinner
            const requestsQuery = query(
              collection(db, 'dinnerRequests'), 
              where('dinnerId', '==', dinnerId),
              where('status', '==', 'accepted')
            );
            const requestsSnapshot = await getDocs(requestsQuery);
            const acceptedRequests = requestsSnapshot.docs.map(doc => doc.data());
            
            console.log('Found accepted requests:', acceptedRequests);
            
            if (acceptedRequests.length > 0) {
              if (acceptedRequests.length === 1) {
                // Single guest - get their specific info
                const guestId = acceptedRequests[0].userId;
                const guestAnswerRef = doc(db, 'shabbotAnswers', guestId);
                const guestAnswerSnap = await getDoc(guestAnswerRef);
                if (guestAnswerSnap.exists()) {
                  guestAnswers = guestAnswerSnap.data().answers || {};
                  guestSigningAs = guestAnswers.signingInAs || "Guest";
                  hasFamily = guestSigningAs === 'Family';
                  console.log('Single guest info:', { guestSigningAs, hasFamily });
                }
              } else {
                // Multiple guests - check if any of them is a family
                guestSigningAs = `${acceptedRequests.length} Guests`;
                for (const request of acceptedRequests) {
                  const guestId = request.userId;
                  const guestAnswerRef = doc(db, 'shabbotAnswers', guestId);
                  const guestAnswerSnap = await getDoc(guestAnswerRef);
                  if (guestAnswerSnap.exists()) {
                    const answers = guestAnswerSnap.data().answers || {};
                    if (answers.signingInAs === 'Family') {
                      hasFamily = true;
                      console.log('Found family among multiple guests');
                      break; // Found a family, no need to check more
                    }
                  }
                }
              }
            }
          } catch (error) {
            console.log('Could not fetch guest request data:', error);
          }
          
          guestInfo = { 
            signingInAs: guestSigningAs,
            shabbotAnswers: guestAnswers,
            hasFamily: hasFamily
          };
        } else {
          // Current user is guest, get host's data
          const hostProfile = await getUserProfile(dinnerData.hostId);
          const hostAnswerRef = doc(db, 'shabbotAnswers', dinnerData.hostId);
          const hostAnswerSnap = await getDoc(hostAnswerRef);
          const hostAnswers = hostAnswerSnap.exists() ? hostAnswerSnap.data().answers : {};
          
          console.log('Host answers:', hostAnswers);
          
          hostInfo = { 
            signingInAs: hostAnswers.signingInAs || "Host",
            shabbotAnswers: hostAnswers 
          };
          guestInfo = { 
            signingInAs: userAnswers.signingInAs || "Guest",
            shabbotAnswers: userAnswers,
            hasFamily: (userAnswers.signingInAs || "Guest") === 'Family'
          };
        }

        const contextPayload = {
          host: hostInfo,
          guest: guestInfo,
          dinner: {
            id: dinnerId,
            kosherType: dinnerData.dinnerDetails?.kosherType,
            mealStyle: dinnerData.dinnerDetails?.mealStyle,
            maxGuests: dinnerData.dinnerDetails?.maxGuests,
            location: `${dinnerData.address?.city || 'Unknown'}, ${dinnerData.address?.country || 'Unknown'}`
          }
        };

        console.log('Context payload:', contextPayload);
        setContextData(contextPayload);
        
        // Automatically check "suitable for children" if either host or guest is a family
        const hostIsFamily = hostInfo.signingInAs === 'Family';
        const guestIsFamily = guestInfo.signingInAs === 'Family' || guestInfo.hasFamily;
        const shouldCheckChildren = hostIsFamily || guestIsFamily;
        
        console.log('Family check:', { hostIsFamily, guestIsFamily, shouldCheckChildren });
        setSuitableForChildren(shouldCheckChildren);
      } catch (err) {
        console.error('Error loading context data:', err);
        setError('Failed to load context data. Using basic mode.');
      } finally {
        setLoadingContext(false);
      }
    };

    loadContextData();
  }, [currentUser, dinnerId, open]);

  // Reset form state when modal opens
  useEffect(() => {
    if (open) {
      setActivityType('either');
      setSuitableForChildren(false); // Will be updated by context loading
      setResult(null);
      setError(null);
    }
  }, [open]);

  // Auto-scroll to results when generated
  useEffect(() => {
    if (result && resultsSectionRef.current) {
      // Small delay to ensure the DOM is updated
      setTimeout(() => {
        resultsSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [result]);

  if (!open) return null;

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      // Call client-side icebreaker with full context (same power as backend!)
      const { generateIcebreaker } = await import('../utils/geminiAPI');
      
      console.log('Calling enhanced client-side icebreaker with context:', {
        activityType,
        suitableForChildren,
        host: contextData?.host || {},
        guest: contextData?.guest || {},
        dinner: contextData?.dinner || {}
      });
      
      const result = await generateIcebreaker({
        dinnerId: dinnerId,
        activityType,
        suitableForChildren,
        host: contextData?.host || {},
        guest: contextData?.guest || {},
        dinner: contextData?.dinner || {}
      });

      if (result.success) {
        // Parse the response more robustly
        let name = 'Personalized Icebreaker';
        let description = result.icebreaker;

        // Try to extract structured response (same parsing as backend)
        const nameMatch = result.icebreaker.match(/Activity Name:\s*(.+?)(?:\n|$)/i);
        const descMatch = result.icebreaker.match(/Description:\s*(.+)/is);
        
        if (nameMatch && descMatch) {
            name = nameMatch[1].trim();
            description = descMatch[1].trim();
        } else {
            // Fallback: try colon separation
            const colonMatch = result.icebreaker.match(/^(.+?)[:：]\s*(.+)$/s);
            if (colonMatch) {
                name = colonMatch[1].trim();
                description = colonMatch[2].trim();
            }
        }

        setResult({ name, description });
      } else {
        throw new Error(result.error || 'Failed to generate icebreaker');
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div className="icebreaker-modal-overlay" onClick={handleOverlayClick}>
      <div className="icebreaker-modal">
        {/* Header */}
        <div className="icebreaker-modal-header">
          <h2 className="icebreaker-modal-title">Icebreaker Generator</h2>
          <p className="icebreaker-modal-subtitle">Create personalized activities for your Shabbat dinner</p>
          <button className="close-btn" onClick={onClose} aria-label="Close modal">
            ×
          </button>
        </div>

        {/* Content */}
        <div className="icebreaker-modal-content">
          {/* Context Information */}
          {contextData && (
            <div className="context-info">
              <div className="context-title">Personalizing for:</div>
              <div className="context-details">
                <strong>Host:</strong> {contextData.host.signingInAs || 'Unknown'} | 
                <strong> Guest:</strong> {contextData.guest.signingInAs || 'Unknown'}
                {contextData.dinner.location && (
                  <><br/><strong>📍 Location:</strong> {contextData.dinner.location}</>
                )}
              </div>
            </div>
          )}
          
          {/* Loading Context */}
          {loadingContext && (
            <div className="loading-context">
              <div className="loading-spinner"></div>
              Loading personality context...
            </div>
          )}
          
          {/* Filters Section */}
          <div className="filters-section">
            <h3 className="filters-title">Activity Preferences</h3>
            
            {/* Activity Type */}
            <div className="activity-type-group">
              <label className="activity-type-label">Activity Type:</label>
              <div className="radio-group">
                {activityTypes.map((type) => (
                  <label 
                    key={type.value} 
                    className={`radio-option ${activityType === type.value ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="activityType"
                      value={type.value}
                      checked={activityType === type.value}
                      onChange={() => setActivityType(type.value)}
                    />
                    {type.label}
                  </label>
                ))}
              </div>
            </div>
            
            {/* Children Suitability */}
            <label className={`checkbox-option ${suitableForChildren ? 'checked' : ''}`}>
              <input
                type="checkbox"
                checked={suitableForChildren}
                onChange={() => setSuitableForChildren((v) => !v)}
              />
              Suitable for children
            </label>
          </div>
          
          {/* Generate Button - only show when no results */}
          {!result && (
            <button
              className="generate-btn"
              onClick={handleGenerate}
              disabled={loading || loadingContext}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Generating...
                </>
              ) : loadingContext ? (
                <>
                  <span className="loading-spinner"></span>
                  Loading context...
                </>
              ) : (
                'Generate Personalized Activity'
              )}
            </button>
          )}
          
          {/* Error Message */}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          {/* Results */}
          {result && (
            <div className="result-section" ref={resultsSectionRef}>
              <div className="result-header">
                <span className="result-icon">🎲</span>
                <h3 className="result-title">{result.name}</h3>
              </div>
              <p className="result-description">{result.description}</p>
              <button
                className="regenerate-btn"
                onClick={handleGenerate}
                disabled={loading}
              >
                {loading ? 'Regenerating...' : 'Generate Another'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}